/** `rule.destructible` — hit points for things that are destroyed rather than
 * respawned (aliens, targets, crates, a docked wingman): every `damage`
 * signal costs its amount; above zero the entity shows a short hit pose; at
 * zero it scores to the seat that fired the blow, signals `defeated` (where it
 * was, its tags) and is removed. `core.vitals` is the player-side consumer of
 * the same `damage` signal. */
import { int, obj } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { addTag } from '../world.js';
import { shooterSlot } from './misc.js';

export type DestructibleConfig = { hp: number; score: number; hitTicks: number };

export const destructible = defineModule<DestructibleConfig>({
  id: 'rule.destructible', version: 1, attach: 'entity', role: 'rule',
  summary: 'Hit points: damage signals cost hp (hit pose while it survives); at zero it scores to the firing seat, signals defeated and is removed.',
  config: obj({ hp: int(1, 99), score: int(0, 100000), hitTicks: int(0, 60) }),
  requires: [], events: ['hit', 'destroy'],
  presentation: { hit: { cue: 'impact', vfx: { effect: 'flash', palette: 'slot' } },
    destroy: { cue: 'destroy', vfx: { effect: 'burst', palette: 'slot', count: 12 } } },
  proofs: ['one-hit-destroys', 'multi-hit-shows-hit-pose', 'credits-firing-seat', 'signals-defeated', 'several-blows-one-tick'],
  // The hit pose lasts `hitTicks`: with none (blue-max's two-hit bridge) it
  // can never be shown, so it is not a state of the kind — declaring it
  // bought a strip no match could ever play (blue-max-composed-4).
  animStates: c => (c.hp > 1 && c.hitTicks > 0 ? ['hit'] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { hp: ctx.config.hp, hit: 0 }); },
  systems: {
    rules(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      if (read(d, 'hit') > 0) d.hit = read(d, 'hit') - 1;
      for (const blow of ctx.signals('damage')) {
        if (blow.target !== e.id) continue;
        const amount = typeof blow.data.amount === 'number' ? blow.data.amount : 1;
        d.hp = read(d, 'hp') - amount;
        const slot = shooterSlot(ctx, blow.source);
        const at = { x: e.x, y: e.y - e.h / 2 };
        if (read(d, 'hp') > 0) {
          d.hit = c.hitTicks;
          ctx.emit('hit', { ...at, slot: slot ?? 0, kind: e.archetype, hp: read(d, 'hp') });
          continue;
        }
        if (slot !== null) ctx.addScore(slot, c.score);
        addTag(e, 'defeated');
        ctx.emit('destroy', { ...at, slot: slot ?? 0, points: c.score, kind: e.archetype });
        ctx.signal('defeated', e, ctx.entity(blow.source) ?? null, { ...at, tags: [...e.tags], kind: e.archetype, slot: slot ?? -1 });
        ctx.despawn(e, 'destroyed');
        return;
      }
    },
    present(ctx, e) {
      if (e && e.status === 'active' && read(ctx.data(e), 'hit') > 0) ctx.setAnim(e, 'hit');
    },
  },
});
